import React, { useState } from 'react';
import { TaxConfigurationService } from '../../../services/taxConfigurationService';

const JurisdictionManager = ({ jurisdictions, organizationId, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    jurisdictionCode: '',
    jurisdictionType: 'state',
    countryCode: 'US',
    stateCode: '',
    countyName: '',
    cityName: '',
    zipCode: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const jurisdictionTypes = [
    { value: 'federal', label: 'Federal' },
    { value: 'state', label: 'State' },
    { value: 'county', label: 'County' },
    { value: 'city', label: 'City' },
    { value: 'local', label: 'Local' },
    { value: 'international', label: 'International' }
  ];

  const countryCodes = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'UK', label: 'United Kingdom' },
    { value: 'AU', label: 'Australia' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'JP', label: 'Japan' },
    { value: 'OTHER', label: 'Other' }
  ];

  const handleCreate = () => {
    setSelectedJurisdiction(null);
    setFormData({
      name: '',
      jurisdictionCode: '',
      jurisdictionType: 'state',
      countryCode: 'US',
      stateCode: '',
      countyName: '',
      cityName: '',
      zipCode: '',
      description: ''
    });
    setErrors({});
    setShowForm(true);
  };

  const handleEdit = (jurisdiction) => {
    setSelectedJurisdiction(jurisdiction);
    setFormData({
      name: jurisdiction?.name || '',
      jurisdictionCode: jurisdiction?.jurisdictionCode || '',
      jurisdictionType: jurisdiction?.jurisdictionType || 'state',
      countryCode: jurisdiction?.countryCode || 'US',
      stateCode: jurisdiction?.stateCode || '',
      countyName: jurisdiction?.countyName || '',
      cityName: jurisdiction?.cityName || '',
      zipCode: jurisdiction?.zipCode || '',
      description: jurisdiction?.description || ''
    });
    setErrors({});
    setShowForm(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Jurisdiction name is required';
    }

    if (!formData?.jurisdictionCode?.trim()) {
      newErrors.jurisdictionCode = 'Jurisdiction code is required';
    }

    if (!formData?.countryCode) {
      newErrors.countryCode = 'Country code is required';
    }

    // Type-specific validation
    if (formData?.jurisdictionType === 'state' && !formData?.stateCode?.trim()) {
      newErrors.stateCode = 'State code is required for state jurisdictions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (selectedJurisdiction) {
        await TaxConfigurationService?.updateJurisdiction(selectedJurisdiction?.id, formData);
      } else {
        await TaxConfigurationService?.createJurisdiction({
          ...formData,
          organizationId
        });
      }
      
      setShowForm(false);
      setSelectedJurisdiction(null);
      await onUpdate();
    } catch (err) {
      console.error('Error saving jurisdiction:', err);
      setErrors({ submit: err?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getJurisdictionIcon = (type) => {
    const icons = {
      federal: '🏛️',
      state: '🗺️',
      county: '🏞️',
      city: '🏙️',
      local: '🏘️',
      international: '🌍'
    };
    return icons?.[type] || '📍';
  };

  const getJurisdictionBadge = (type) => {
    const typeStyles = {
      federal: 'bg-blue-100 text-blue-800',
      state: 'bg-purple-100 text-purple-800',
      county: 'bg-indigo-100 text-indigo-800',
      city: 'bg-pink-100 text-pink-800',
      local: 'bg-orange-100 text-orange-800',
      international: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${typeStyles?.[type] || typeStyles?.local}`}>
        {type?.charAt(0)?.toUpperCase() + type?.slice(1) || 'Unknown'}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Tax Jurisdictions</h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage geographic areas for tax calculation and compliance
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Add Jurisdiction</span>
          </button>
        </div>
      </div>
      {/* Jurisdictions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {jurisdictions?.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
            <div className="flex flex-col items-center">
              <span className="text-4xl mb-4">🗺️</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No jurisdictions found</h3>
              <p className="text-sm text-gray-500 mb-4">
                Get started by creating your first tax jurisdiction
              </p>
              <button
                onClick={handleCreate}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Create Jurisdiction
              </button>
            </div>
          </div>
        ) : (
          jurisdictions?.map((jurisdiction) => (
            <div key={jurisdiction?.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getJurisdictionIcon(jurisdiction?.jurisdictionType)}</span>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{jurisdiction?.name}</h3>
                      <p className="text-sm text-gray-500">{jurisdiction?.jurisdictionCode}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleEdit(jurisdiction)}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    title="Edit jurisdiction"
                  >
                    ✏️
                  </button>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Type:</span>
                    {getJurisdictionBadge(jurisdiction?.jurisdictionType)}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Country:</span>
                    <span className="text-sm text-gray-900">{jurisdiction?.countryCode}</span>
                  </div>
                  
                  {jurisdiction?.stateCode && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">State:</span>
                      <span className="text-sm text-gray-900">{jurisdiction?.stateCode}</span>
                    </div>
                  )}
                  
                  {jurisdiction?.countyName && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">County:</span>
                      <span className="text-sm text-gray-900">{jurisdiction?.countyName}</span>
                    </div>
                  )}
                  
                  {jurisdiction?.cityName && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">City:</span>
                      <span className="text-sm text-gray-900">{jurisdiction?.cityName}</span>
                    </div>
                  )}
                </div>

                {jurisdiction?.description && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600">{jurisdiction?.description}</p>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                  <span>Tax Rates: {jurisdiction?.taxRatesCount || 0}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    jurisdiction?.isActive 
                      ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                  }`}>
                    {jurisdiction?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedJurisdiction ? 'Edit Jurisdiction' : 'Create New Jurisdiction'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Jurisdiction Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      errors?.name 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="e.g., California State"
                  />
                  {errors?.name && (
                    <p className="mt-1 text-sm text-red-600">{errors?.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="jurisdictionCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Jurisdiction Code *
                  </label>
                  <input
                    type="text"
                    id="jurisdictionCode"
                    value={formData?.jurisdictionCode}
                    onChange={(e) => handleInputChange('jurisdictionCode', e?.target?.value?.toUpperCase())}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      errors?.jurisdictionCode 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="e.g., CA-STATE"
                  />
                  {errors?.jurisdictionCode && (
                    <p className="mt-1 text-sm text-red-600">{errors?.jurisdictionCode}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="jurisdictionType" className="block text-sm font-medium text-gray-700 mb-2">
                    Jurisdiction Type
                  </label>
                  <select
                    id="jurisdictionType"
                    value={formData?.jurisdictionType}
                    onChange={(e) => handleInputChange('jurisdictionType', e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {jurisdictionTypes?.map((type) => (
                      <option key={type?.value} value={type?.value}>
                        {type?.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="countryCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <select
                    id="countryCode"
                    value={formData?.countryCode}
                    onChange={(e) => handleInputChange('countryCode', e?.target?.value)}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      errors?.countryCode 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  >
                    {countryCodes?.map((country) => (
                      <option key={country?.value} value={country?.value}>
                        {country?.label}
                      </option>
                    ))}
                  </select>
                  {errors?.countryCode && (
                    <p className="mt-1 text-sm text-red-600">{errors?.countryCode}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="stateCode" className="block text-sm font-medium text-gray-700 mb-2">
                    State Code {formData?.jurisdictionType === 'state' && '*'}
                  </label>
                  <input
                    type="text"
                    id="stateCode"
                    value={formData?.stateCode}
                    onChange={(e) => handleInputChange('stateCode', e?.target?.value?.toUpperCase())}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      errors?.stateCode 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="e.g., CA"
                  />
                  {errors?.stateCode && (
                    <p className="mt-1 text-sm text-red-600">{errors?.stateCode}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="countyName" className="block text-sm font-medium text-gray-700 mb-2">
                    County Name
                  </label>
                  <input
                    type="text"
                    id="countyName"
                    value={formData?.countyName}
                    onChange={(e) => handleInputChange('countyName', e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Los Angeles County"
                  />
                </div>

                <div>
                  <label htmlFor="cityName" className="block text-sm font-medium text-gray-700 mb-2">
                    City Name
                  </label>
                  <input
                    type="text"
                    id="cityName"
                    value={formData?.cityName}
                    onChange={(e) => handleInputChange('cityName', e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Los Angeles"
                  />
                </div>

                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    value={formData?.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 90210"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Additional information about this jurisdiction..."
                />
              </div>

              {errors?.submit && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-600">{errors?.submit}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    loading 
                      ? 'bg-gray-400 cursor-not-allowed' :'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {loading ? 'Saving...' : (selectedJurisdiction ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JurisdictionManager;